import React from 'react';
import { Card, Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaStar, FaRegStar } from 'react-icons/fa';
import RecommendationReason from './RecommendationReason';
import RatingStars from './RatingStars';
import { t } from '../utils/i18n';
import { formatRating, getRatingColor, getRatingValue } from '../utils/ratingUtils';
import { getGoogleSearchUrl, getYouTubeMusicUrl } from '../utils/linkUtils';
import { formatRelativeDate, parseGenres } from '../utils/dataUtils';

function typeLabel(type, language) {
  if (type === 'series') return t('cards.typeSeries', language);
  if (type === 'music') return t('cards.typeMusic', language);
  return t('cards.typeMovie', language);
}

/**
 * Compact recommendation card for the library grid.
 */
function RecommendationCard({
  rec,
  language,
  isFavorite,
  favoriteLoading,
  onToggleFavorite,
  onMarkAsWatched,
  onOmit,
  onReactivate,
  isNew,
}) {
  const type = rec.type || 'movie';
  const href =
    type === 'music'
      ? getYouTubeMusicUrl(rec.title, { year: rec.year, artist: rec.artist })
      : getGoogleSearchUrl(rec.title, { year: rec.year, artist: rec.artist });

  const avg = getRatingValue(rec, 'average_rating');
  const ext = getRatingValue(rec, 'external_rating');
  const ai = getRatingValue(rec, 'ai_rating');
  const genres = parseGenres(rec.genre).slice(0, 2);
  const isOmitted = (rec.status || 'active') === 'omitted';

  const favoriteButton = (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>
          {favoriteLoading
            ? t('recommendations.processing', language)
            : isFavorite
              ? t('recommendations.removeFromFavorites', language)
              : t('recommendations.addToFavorites', language)}
        </Tooltip>
      }
    >
      <Button
        variant="link"
        className={`miskosas-content-card__btn-favorite p-0 ${isFavorite ? 'is-active' : ''}`}
        onClick={(e) => onToggleFavorite(rec, e)}
        disabled={favoriteLoading}
        aria-label={
          isFavorite
            ? t('recommendations.removeFromFavorites', language)
            : t('recommendations.addToFavorites', language)
        }
      >
        {favoriteLoading ? (
          <Spinner animation="border" size="sm" style={{ width: 18, height: 18 }} />
        ) : isFavorite ? (
          <FaStar size={20} />
        ) : (
          <FaRegStar size={20} />
        )}
      </Button>
    </OverlayTrigger>
  );

  return (
    <Card
      className={`h-100 miskosas-content-card miskosas-content-card--${type} recommendation-card ${
        isFavorite ? 'is-favorite' : ''
      }`}
    >
      <Card.Body>
        <div className="miskosas-content-card__top">
          <span className="miskosas-content-card__type-chip">{typeLabel(type, language)}</span>
          {favoriteButton}
        </div>

        <h3 className="miskosas-content-card__title">
          <a href={href} target="_blank" rel="noopener noreferrer">
            {rec.title}
          </a>
        </h3>

        {rec.artist ? (
          <p className="miskosas-content-card__subtitle">{rec.artist}</p>
        ) : null}

        <div className="miskosas-content-card__tags">
          {genres.map((g) => (
            <span key={g} className="miskosas-content-card__tag">
              {g}
            </span>
          ))}
          {rec.year ? <span className="miskosas-content-card__tag">{rec.year}</span> : null}
          {isNew ? (
            <span className="miskosas-content-card__tag miskosas-content-card__tag--status-new">
              {t('recommendations.new', language)}
            </span>
          ) : null}
          {isOmitted ? (
            <span className="miskosas-content-card__tag miskosas-content-card__tag--status-omitted">
              {t('recommendations.omitted', language)}
            </span>
          ) : null}
        </div>

        <div className="miskosas-content-card__rating-row">
          <div className={`miskosas-content-card__rating-main ${getRatingColor(avg)}`}>
            {formatRating(avg)}
          </div>
          <div className="miskosas-content-card__rating-details">
            <div className="d-flex align-items-center gap-1 mb-1">
              <RatingStars rating={avg} size={13} />
            </div>
            <span>
              {t('recommendations.externalRatingLabel', language)}:{' '}
              <strong className={getRatingColor(ext)}>{formatRating(ext)}</strong>
              {' · '}
              {t('recommendations.aiRatingLabel', language)}:{' '}
              <strong className={getRatingColor(ai)}>{formatRating(ai)}</strong>
            </span>
          </div>
        </div>

        {rec.reason ? (
          <div className="miskosas-content-card__reason">
            <RecommendationReason reason={rec.reason} language={language} />
          </div>
        ) : null}

        {type !== 'music' && rec.actors?.length > 0 ? (
          <p className="miskosas-content-card__meta-line">
            {t('recommendations.actors', language)}: {rec.actors.slice(0, 2).join(', ')}
            {rec.actors.length > 2 ? '…' : ''}
          </p>
        ) : null}

        <p className="miskosas-content-card__meta-line mb-0">
          {t('recommendations.recommendedAt', language)}{' '}
          {formatRelativeDate(rec.recommended_at || rec.recommendedAt, language) ?? '—'}
        </p>

        <div className="miskosas-content-card__footer">
          <Button
            className="btn-miskosas-primary flex-grow-1"
            size="sm"
            onClick={() => onMarkAsWatched(rec)}
          >
            {t('recommendations.markAsWatched', language)}
          </Button>
          {!isOmitted ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className="miskosas-content-card__btn-icon"
              onClick={() => onOmit(rec)}
              title={t('recommendations.omit', language)}
              aria-label={t('recommendations.omit', language)}
            >
              ✕
            </Button>
          ) : (
            <Button
              variant="outline-success"
              size="sm"
              className="miskosas-content-card__btn-icon"
              onClick={() => onReactivate(rec)}
              title={t('recommendations.reactivate', language)}
              aria-label={t('recommendations.reactivate', language)}
            >
              ↻
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

export default RecommendationCard;
